/**
 * Auth and onboarding routes do not show a balance. Skip billing-getTotalCredits
 * and subscription-retrieve while the user is on one of these paths, including
 * the root spinner that redirects before /video.
 *
 * After a new signup over an existing session, also refuse to retrieve another
 * user's subscription — see resolveSelfSubscriptionRetrieveUid.
 */
export const AUTH_ONBOARDING_BILLING_SKIP_PATHS = [
    "/",
    "/signup",
    "/login",
    "/email-verification",
    "/resetPassword",
    "/password-reset",
    "/onboard",
    "/onboard-pricing",
    "/setup-channel",
    "/setup-channel-languages",
    "/setup-channel-languages-add",
    "/setup-branding",
] as const;

export function normalizeAppPathname(pathname: string): string {
    const withoutHash = pathname.split("#")[0] ?? "";
    const withoutQuery = withoutHash.split("?")[0] ?? "";
    if (withoutQuery.length > 1 && withoutQuery.endsWith("/")) {
        return withoutQuery.slice(0, -1);
    }
    return withoutQuery || "/";
}

export function shouldSkipBillingFetchForPath(pathname: string | null | undefined): boolean {
    if (!pathname) {
        return true;
    }
    const normalized = normalizeAppPathname(pathname);
    return (AUTH_ONBOARDING_BILLING_SKIP_PATHS as readonly string[]).includes(normalized);
}

/**
 * Self-service `subscription-retrieve` must use the signed-in Firebase uid.
 * A stale React `user.uid` after account switch (signup over an existing
 * session) would ask for another user's subscription and get permission-denied.
 */
export function resolveSelfSubscriptionRetrieveUid(
    requestedUid: string | null | undefined,
    authenticatedUid: string | null | undefined,
): string | null {
    if (!requestedUid || !authenticatedUid) {
        return null;
    }
    if (requestedUid !== authenticatedUid) {
        return null;
    }
    return authenticatedUid;
}
